/**
 * Browser mode utility functions.
 */

/**
 * Product name for browser mode branding.
 * The official product name is "Jam SQL Studio" (domain: jamsql.com).
 * The codebase name remains "sql-studio".
 */
export const PRODUCT_NAME = 'Jam SQL Studio';

/**
 * Check if the app is running in browser mode.
 *
 * Browser mode is detected when:
 * - Running in a browser (window exists)
 * - VITE_WEB_DEMO env variable is set (build-time)
 * - OR electronAPI is not the real Electron API
 */
export const isBrowserMode = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  // Check build-time environment variable
  if (import.meta.env.VITE_WEB_DEMO === true || import.meta.env.VITE_WEB_DEMO === 'true') {
    return true;
  }

  // Fallback: Check if electronAPI looks like a shim (no real Electron)
  // Real Electron would have testMode.isTestMode return boolean based on actual test mode
  // Our shim always returns false for isTestMode, but real Electron in dev also returns false
  // So we also check for the presence of a specific marker we can add
  return false;
};

/**
 * Check if browser mode should have Pro features enabled.
 *
 * In browser mode, users get a full Pro experience for SQLite to showcase
 * the app's capabilities. This removes upgrade prompts and trial dialogs.
 *
 * Desktop download is encouraged for additional database support (SQL Server, PostgreSQL).
 */
export const isBrowserModePro = (): boolean => {
  return isBrowserMode();
};

/**
 * Check if the app is running in embed mode.
 *
 * Embed mode is used when the browser demo is embedded in an iframe on the homepage.
 * It auto-connects to Chinook database and hides the connections dialog.
 *
 * Detected via `?embed=true` query parameter.
 */
export const isEmbedMode = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  return params.get('embed') === 'true';
};

/**
 * Check if a feature is available in the current environment.
 * Returns false for features that require Electron in browser mode.
 */
export const isFeatureAvailable = (
  feature: 'terminal' | 'fileSystem' | 'mssql' | 'pgsql' | 'entraAuth' | 'backup'
): boolean => {
  if (!isBrowserMode()) {
    return true; // All features available in Electron
  }

  // In browser mode, only SQLite is available
  const unavailableInBrowser = ['terminal', 'fileSystem', 'mssql', 'pgsql', 'entraAuth', 'backup'];
  return !unavailableInBrowser.includes(feature);
};

/**
 * Generate a unique connection name based on existing connections.
 * If the base name already exists, appends a number suffix like "(1)", "(2)", etc.
 *
 * @param baseName - The base connection name (e.g., file name)
 * @param existingNames - Array of existing connection names to check against
 * @returns A unique connection name
 */
export const generateUniqueConnectionName = (
  baseName: string,
  existingNames: string[]
): string => {
  // Check if the base name exists
  if (!existingNames.includes(baseName)) {
    return baseName;
  }

  // Find the next available suffix
  let suffix = 1;
  while (existingNames.includes(`${baseName} (${suffix})`)) {
    suffix++;
  }

  return `${baseName} (${suffix})`;
};
